import { useState, useCallback, useEffect } from "react";
import { Asset } from "@switcherstudio/switcher-api-client";
import { client } from "api/client";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store/reducers";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { setLoading } from "store/loading/slice";
import rollbar from "helpers/rollbar";
import { useTranslation } from "react-i18next";
import { AppDispatch } from "store/store";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { AttentionModal } from "components/modal/AttentionModal";

export function useAssetDetails(assetId: string) {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { userInfo } = useSelector((s: RootState) => s.user);
    const [asset, setAsset] = useState<Asset>();
    const [rawFiles, setRawFiles] = useState<Asset[]>([]);
    const [otherAssets, setOtherAssets] = useState<Asset[]>([]);
    const [deleteRawFiles, setDeleteRawFiles] = useState<boolean>(false);
    const [disableDeleteRawFiles, setDisableDeleteRawFiles] =
        useState<boolean>(false);

    useEffect(() => {
        async function run() {
            try {
                dispatch(setLoading(1));
                const asset = await client.userAssets_GetUserAsset(
                    userInfo?.User?.Details?.Id || "",
                    assetId
                );

                if (asset.RequiresAssetIds) {
                    let rawFiles = [];
                    let otherAssets = [];

                    for (const rawFileId of asset.RequiresAssetIds) {
                        const rawFile = await client.userAssets_GetUserAsset(
                            userInfo?.User?.Details?.Id || "",
                            rawFileId
                        );

                        if (rawFile.RequiredByAssetIds?.length) {
                            const otherAssetIds =
                                rawFile.RequiredByAssetIds.filter(
                                    (id) => id !== asset.Id
                                );

                            for (const id of otherAssetIds) {
                                const otherAsset =
                                    await client.userAssets_GetUserAsset(
                                        userInfo?.User?.Details?.Id || "",
                                        id
                                    );
                                otherAssets.push(otherAsset);
                            }
                        }

                        rawFiles.push(rawFile);
                    }

                    let disable = false;

                    const rawFilesIds = rawFiles.map((rf) => rf.Id);

                    for (let oa of otherAssets) {
                        const usesOtherFiles = oa.RequiresAssetIds?.some(
                            (id) => !rawFilesIds.includes(id)
                        );
                        if (usesOtherFiles) {
                            disable = true;
                        }
                    }

                    if (disable) {
                        setDeleteRawFiles(false);
                        setDisableDeleteRawFiles(disable);
                    } else {
                        setDeleteRawFiles(true);
                        setDisableDeleteRawFiles(disable);
                    }

                    setRawFiles(rawFiles);
                    setOtherAssets(otherAssets);
                }
                setAsset(asset);
                dispatch(setLoading(-1));
            } catch (e) {
                rollbar.error("Error retrieving asset", e);
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "errors:asset-retrieval-error"
                    })
                );
                dispatch(setLoading(-1));
            }
        }

        run();
    }, [dispatch, assetId, userInfo, t]);

    const closeAttentionModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const remove = useCallback(async () => {
        if (asset) {
            dispatch(
                setActiveModal({
                    id: Modals.AttentionModal,
                    type: Modals.AttentionModal,
                    component: (
                        <AttentionModal
                            isOpen={true}
                            setIsOpen={closeAttentionModal}
                            handleCancel={closeAttentionModal}
                            hasContinueButton={true}
                            handleContinue={async () => {
                                try {
                                    let payload = [asset];
                                    if (deleteRawFiles)
                                        payload = [
                                            ...payload,
                                            ...rawFiles,
                                            ...otherAssets
                                        ];

                                    dispatch(setLoading(1));
                                    await client.userAssets_DeleteUserAssetsAll(
                                        userInfo?.User?.Details?.Id || "",
                                        payload
                                    );
                                    dispatch(setLoading(-1));
                                    navigate("/switcher-cloud");
                                } catch (e) {
                                    rollbar.error("Error removing asset", e);
                                    dispatch(
                                        addNotification({
                                            type: NotificationType.Danger,
                                            message:
                                                "errors:asset-removal-error"
                                        })
                                    );
                                    dispatch(setLoading(-1));
                                }
                            }}
                            continueText={t("buttons:confirm")}
                        >
                            <div>
                                {deleteRawFiles ? (
                                    <>
                                        <p>{t("asset-hook:confirm-1")}</p>

                                        {rawFiles.length > 0 && (
                                            <>
                                                <p>
                                                    {t("asset-hook:confirm-2")}
                                                </p>
                                                <ul>
                                                    {rawFiles.map((a) => (
                                                        <li key={a.Id}>
                                                            {a.Name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        )}

                                        {otherAssets.length > 0 && (
                                            <div>
                                                <p>
                                                    {t("asset-hook:confirm-3")}
                                                </p>
                                                <ul>
                                                    {otherAssets.map((a) => (
                                                        <li key={a.Id}>
                                                            {a.Name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    t("messages:delete-asset-confirmation")
                                )}
                            </div>
                        </AttentionModal>
                    )
                })
            );
        }
    }, [
        dispatch,
        asset,
        navigate,
        rawFiles,
        otherAssets,
        deleteRawFiles,
        userInfo,
        t,
        closeAttentionModal
    ]);

    const toggleDeleteRawFiles = useCallback(() => {
        setDeleteRawFiles(!deleteRawFiles);
    }, [deleteRawFiles]);

    return {
        asset,
        rawFiles,
        otherAssets,
        deleteRawFiles,
        disableDeleteRawFiles,
        remove,
        toggleDeleteRawFiles
    };
}
